(ns admin.components.learner-reports.learning-objectives
  (:require
   [applied-science.js-interop :as j]
   ["d3" :as d3]
   [reagent.core :refer [create-class] :as reagent]
   [goog.math :as gmath]
   [goog.string :as gstring]
   [goog.string.format]
   [potpuri.core :as pot]

   [admin.components.learner-reports.shared :as lrs]

   [kyber.styles.core :refer [styles->classes gs]]
   [kyber.components.base.text :as text]
   [kyber.components.composite.expander :as expander]
   [kyber.utils :as ku]))

(def classes
  (styles->classes
   {:learning-objective-container
    {:border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))

     "&:last-child"
     {:border-bottom :none
      :padding-bottom 0}}

    :lo-header
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :width "100%"
     :min-height (gs [:spacing :p52])}

    :lo-header-left
    {:display :flex
     :align-items :center

     "& > *:first-child"
     {:margin-right (gs [:spacing :p12])}}

    :readiness-label
    {:margin-right (gs [:spacing :p12])}

    :readiness-pill
    {:color (gs [:colors :true-white])
     :min-width "70px"
     :text-align :center
     :background-color (gs [:colors :primary-lite-600])
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :border-radius (gs [:radius :r40])}

    :message-container
    {:width "100%"
     :margin-bottom (gs [:spacing :p20])
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :border (str "2px solid " (gs [:colors :true-white]))

     "& p"
     {:font-style :italic}}

    :error-message-container
    {:background (gs [:colors :error-50])
     :border-color (str (gs [:colors :error-300]) " !important")

     "& p"
     {:color (gs [:colors :error-900])}}

    :warning-message-container
    {:background (gs [:colors :warning-50])
     :border-color (str (gs [:colors :warning-300]) " !important")

     "& p"
     {:color (gs [:colors :warning-900])}}

    :pre-post-test-container
    {:display :flex
     :justify-content :space-between
     :align-items :flex-end
     :width "60%"

     "& > *"
     {:width "calc(50 - 10px)"}}

    :no-test-copy
    {:text-align :center
     :max-width "200px"
     :margin :auto
     :margin-top (gs [:spacing :p28])}

    :test-tooltip
    {:border-radius (gs [:radius :r3])
     :background (gs [:colors :true-white])
     :border (str "1px solid " (gs [:colors :gray-scale-100]))
     :box-shadow (gs [:shadows :shadow-6])
     :padding [(gs [:spacing :p2])
               (gs [:spacing :p8])]}

    :gauge-chart-container
    {:width "100%"
     :display :flex
     :justify-content :center}

    :learning-objective-charts-container
    {:display :grid
     :grid-gap :1rem
     :grid-template-columns "repeat(auto-fit, minmax(18rem, 1fr))"

     (gs [:queries :ie])
     {:display :block}}

    :axis
    {:fill :none
     :stroke (gs [:colors :true-black])
     :shape-rendering :crispEdges}

    :dot-label
    {:font-weight (gs [:font-weight :title-10])
     :font-family (gs [:font-family :open-sans])}}))

(defn- clear-scatter-plot!
  [el]
  (-> d3
      (j/call :select el)
      (j/call :selectAll "*")
      (j/call :remove)))

(defn- draw-scatter-plot!
  [el chart-api data]

  ;; Clear existing children if called more than once
  (clear-scatter-plot! el)

  (let [{:keys [width height margin y-max y-min x-max x-min r]
         :or {margin {:top 20 :right 20 :bottom 30 :left 40}
              r 10}}
        @chart-api

        calculated-width
        (- width (:left margin) (:right margin))

        calculated-height
        (- height (:top margin) (:bottom margin))

        x-domain
        (if (and (some? x-min)
                 (some? x-max))
          #js [x-min x-max]
          (j/call d3 :extent data #(j/get %1 :x)))

        y-domain
        (if (and (some? y-min)
                 (some? y-max))
          #js [y-min y-max]
          (j/call d3 :extent data #(j/get %1 :y)))

        ;; x
        x (-> d3
              (j/call :scaleLinear)
              (j/call :range #js [0 calculated-width]))
        x-axis (j/call d3 :axisBottom x)

        y-ticks
        (* 2 (- (last y-domain)
                (first y-domain)))

        ;; y
        y (-> d3
              (j/call :scaleLinear)
              (j/call :range #js [calculated-height 0]))
        y-axis (-> d3
                   (j/call :axisLeft y)
                   ;; This controls how many tick marks are displayed on the chart
                   ;; Max number (at the time) is 12 because of where it's rendered.
                   ;; The forumla above calculates and will render every half step.
                   (j/call :ticks (if (> y-ticks 12) 12 y-ticks))
                   (j/call :tickSize (* -1 width)))

        svg
        (-> d3
            (j/call :select el)
            (j/call :append "svg:svg")
            (j/call :attr "height" (+ calculated-height (:top margin) (:bottom margin)))
            (j/call :attr "width" (+ calculated-width (:left margin) (:right margin)))
            (j/call :append "g")
            (j/call :attr "transform" (str "translate(" (:left margin) "," (:right margin) ")")))]

    (-> x
        (j/call :domain x-domain)
        (j/call :nice))

    (-> y
        (j/call :domain y-domain)
        (j/call :nice))

    ;; Draw x axis
    (-> svg
        (j/call :append "g")
        (j/call :attr "class" (:axis classes))
        (j/call :attr "transform" (str "translate(0," height ")"))
        (j/call :call x-axis)
        (j/call :append "text")
        (j/call :attr "class" (:axis-label classes))
        (j/call :attr "x" calculated-width)
        (j/call :attr "y" -6)
        (j/call :style "text-anchor" "end")
        (j/call :text "Questions"))

    ;; Draw y axis
    (-> svg
        (j/call :append "g")
        (j/call :attr "class" (:axis classes))
        (j/call :call y-axis)
        (j/call :append "text")
        (j/call :attr "class" (:axis-label classes))
        (j/call :attr "transform" "rotate(-90)")
        (j/call :attr "y" -40)
        (j/call :attr "x" -20)
        (j/call :attr "dy" ".71em")
        (j/call :style "text-anchor" "end")
        (j/call :text "Correct Count"))

    ;; Dots
    (-> svg
        (j/call :selectAll (:dot classes))
        (j/call :data data)
        (j/call :enter)
        (j/call :append "circle")
        (j/call :attr "class" (:dot classes))
        (j/call :attr "r" r)
        (j/call :attr "cx" #(x (j/get %1 :x)))
        (j/call :attr "cy" #(y (j/get %1 :y)))
        (j/call :style "fill" #(j/get %1 :color (gs [:colors :gray-scale-400]))))

    ;; Dot Text
    (-> svg
        (j/call :selectAll (:dot classes))
        (j/call :data data)
        (j/call :enter)
        (j/call :append "text")
        (j/call :text #(gstring/format "%.1f" (or (j/get %1 :y) 0)))
        (j/call :attr "class" (:dot-label classes))
        (j/call :attr "x" #(- (x (j/get %1 :x)) (/ r 2)))
        (j/call :attr "y" #(+ (y (j/get %1 :y)) 4)) ;; Center text in bubble (TODO Need to make this more dynamic)
        (j/call :style "fill" (gs [:colors :true-white])))))

(defn- scatter-plot
  [{{:keys [avg-peer-correct-count learner-correct-count total-graded-question-count]} :data
    {:keys [supports-aggregates]} :options}]
  (let [initial-data (clj->js (cond-> [{:x 1 :y learner-correct-count
                                        :color (gs [:colors :primary-lite-500])}]
                                (true? supports-aggregates)
                                (conj {:x 2 :y avg-peer-correct-count})))
        !ref (atom nil)
        chart-api (atom {:width nil
                         :height nil
                         :y-min 0
                         :y-max total-graded-question-count
                         :x-min 0
                         :x-max (if supports-aggregates 3 2)
                         :r 16})
        resize-chart! (fn [_]
                        (swap! chart-api assoc
                               :width (j/get @!ref :clientWidth)
                               :height (j/get @!ref :clientHeight))
                        (draw-scatter-plot! @!ref chart-api initial-data))
        resize-chart-debounced! (ku/debounce resize-chart! 300)]
    (create-class
     {:display-name
      "scatterplot"

      :component-did-mount
      (fn [_]
        (j/call js/window :addEventListener "resize" resize-chart-debounced!)
        (resize-chart! nil))

      :component-will-unmount
      (fn [_]
        (j/call js/window :removeEventListener "resize" resize-chart-debounced!))

      :reagent-render
      (fn [{{:keys [avg-peer-correct-count learner-correct-count]} :data
            {:keys [supports-aggregates]} :options}]
        (let [data (clj->js (cond-> [{:x 1 :y learner-correct-count :color (gs [:colors :primary-lite-500])}]
                              (true? supports-aggregates)
                              (conj {:x 2 :y avg-peer-correct-count})))
              _ (draw-scatter-plot! @!ref chart-api data)]
          [:div {:style {:height "100%"
                         :width "100%"}
                 :ref #(reset! !ref %)}]))})))

(defn- render-learning-objective-header
  [{:learning-objective/keys [label readiness readiness-label]}]
  [:div {:class (:lo-header classes)}
   [:div {:class (:lo-header-left classes)}
    [text/caption-30 :p label]]
   [lrs/readiness-bubble readiness readiness-label]])

(defn- render-message-group [{:keys [messages status]}]
  (when (seq messages)
    [:ul {:class [(:message-container classes)
                  (get classes (keyword (str (name status)
                                             "-message-container")))]}
     (for [{:keys [value]} messages]
       ^{:key value}
       [:li [text/caption-30 :p (str "- " value)]])]))

(defn- render-learning-objective-messages [messages]
  [:<>
   (render-message-group
    {:messages (filter #(-> % :status (= "error")) messages)
     :status :error})

   (render-message-group
    {:messages (filter #(-> % :status (= "warning")) messages)
     :status :warning})])

(defn- render-learning-objective-test [{:keys [title data no-data-message options]}]
  [:div {:style {:width "100%"
                 :height "220px"
                 :display :flex
                 :flex-direction :column}}
   [text/title-30 :p title]

   (if (some? data)
     [scatter-plot {:data data :options options}]
     [text/caption-30 :p
      {:class (:no-test-copy classes)}
      no-data-message])])

(defn- readiness-score->gauge-angle [readiness-score]
  (let [max-readiness-score 10
        readiness-percent (/ readiness-score max-readiness-score)]
    (- (* readiness-percent 180) 90)))

(defn- initialize-gauge
  ([] (initialize-gauge {}))
  ([{:keys [clip-width
            clip-height
            ring-inset
            ring-width
            min-angle
            max-angle
            size]
     :or {clip-width 300
          clip-height 180
          ring-inset 20
          ring-width 20
          min-angle -90
          max-angle 90
          size 300}}]
   (let [radius (/ size 2)
         learner-arc-fn
         (-> d3
             (j/call :arc)
             (j/call :innerRadius (- radius ring-width ring-inset))
             (j/call :outerRadius (- radius ring-inset))
             (j/call :startAngle (gmath/toRadians min-angle))
             (j/call :endAngle gmath/toRadians))

         peer-arc-fn
         (-> d3
             (j/call :arc)
             (j/call :innerRadius (- radius (* ring-width 2) ring-inset))
             (j/call :outerRadius (- radius ring-inset ring-width))
             (j/call :startAngle (gmath/toRadians min-angle))
             (j/call :endAngle gmath/toRadians))]

     {:peer-arc-fn peer-arc-fn
      :learner-arc-fn learner-arc-fn
      :clip-height clip-height
      :clip-width clip-width
      :radius radius
      :min-angle min-angle
      :max-angle max-angle
      :size size})))

(defn- draw-arc-background!
  [{:keys [max-angle arc-fn center-tx background-color svg]
    :or {background-color (gs [:colors :gray-scale-200])}}]
  (-> svg
      (j/call :append "path")
      (j/call :datum max-angle)
      (j/call :attr "d" arc-fn)
      (j/call :style "fill" background-color)
      (j/call :attr "transform" center-tx)))

(defn- draw-readiness-arc-data!
  [{:keys [svg readiness arc-fn center-tx background]}]
  (-> svg
      (j/call :append "path")
      (j/call :datum (readiness-score->gauge-angle readiness))
      (j/call :attr "d" arc-fn)
      (j/call :style "fill" background)
      (j/call :attr "transform" center-tx)))

(defn- draw-legend-item!
  [{:keys [svg
           clip-width
           clip-height
           color
           y-offset
           label]}]
  (let [text-padding 4]
    (-> svg
        (j/call :append "circle")
        (j/call :attr "cx" (- (/ clip-width 2) 40))
        (j/call :attr "cy" (- clip-height y-offset text-padding))
        (j/call :attr "r" 6)
        (j/call :style "fill" color)))

  (-> svg
      (j/call :append "text")
      (j/call :attr "x" (- (/ clip-width 2) 30))
      (j/call :attr "y" (- clip-height y-offset))
      (j/call :text label)))

(defn- draw-gauge!
  [el
   guage-api
   {:keys [learner-readiness average-peer-readiness]}
   {:keys [supports-aggregates]}]
  (let [{:keys [peer-arc-fn
                learner-arc-fn
                clip-width
                clip-height
                radius
                max-angle]}
        @guage-api

        svg
        (-> d3
            (j/call :select el)
            (j/call :append "svg:svg")
            (j/call :attr "width" clip-width)
            (j/call :attr "height" clip-height))

        center-tx
        (str "translate(" radius "," radius ")")]

    ;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;
    ;; Draw Backgrounds
    ;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;

    ;; peer background
    (when supports-aggregates
      (draw-arc-background! {:svg svg
                             :max-angle max-angle
                             :arc-fn peer-arc-fn
                             :center-tx center-tx}))
    ;; learner background
    (draw-arc-background! {:svg svg
                           :max-angle max-angle
                           :arc-fn learner-arc-fn
                           :center-tx center-tx})

    ;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;
    ;; Draw Data
    ;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;

    ;; peer data
    (when supports-aggregates
      (draw-readiness-arc-data! {:svg svg
                                 :readiness average-peer-readiness
                                 :arc-fn peer-arc-fn
                                 :center-tx center-tx
                                 :background (gs [:colors :gray-scale-400])}))
    ;; learner data
    (draw-readiness-arc-data! {:svg svg
                               :readiness learner-readiness
                               :arc-fn learner-arc-fn
                               :center-tx center-tx
                               :background (gs [:colors :primary-lite-500])})

    ;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;
    ;; Draw Legend
    ;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;

    ;; peer legend
    (when supports-aggregates
      (draw-legend-item! {:svg svg
                          :clip-width clip-width
                          :clip-height clip-height
                          :color (gs [:colors :gray-scale-400])
                          :y-offset 40
                          :label (str "Peer Avg. " (lrs/format-readiness average-peer-readiness))}))

    ;; learner legend
    (draw-legend-item! {:svg svg
                        :clip-width clip-width
                        :clip-height clip-height
                        :color (gs [:colors :primary-lite-500])
                        :y-offset 60
                        :label (str "Learner " (lrs/format-readiness learner-readiness))})))

(defn- render-double-gauge
  [data options]
  (let [!ref (atom nil)
        gauge-api (atom nil)]
    (create-class
     {:display-name
      "double-gauge"

      :component-did-mount
      (fn [_]
        (reset! gauge-api (initialize-gauge))
        (draw-gauge! @!ref gauge-api data options))

      :reagent-render
      (fn [_]
        [:div {:class (:gauge-chart-container classes)
               :ref #(reset! !ref %)}])})))

(defn- render-readiness-gauge [{:keys [learner-readiness title] :as data} options]
  [:div {:class (:gauge-container classes)}
   [text/title-30 :p "Readiness"]
   [text/caption-20 :p "Learner/Peer comparison"]
   (if (some? learner-readiness)
     [render-double-gauge data options]
     [text/caption-30 :p
      {:class (:no-test-copy classes)}
      (str "The readiness score requires at least one Post-Test question "
           "assigned to "
           title ".")])])

(defn- render-learning-objective-body
  [{:learning-objective/keys [first-pre-test-correct-count
                              first-post-test-correct-count
                              first-pre-test-count
                              first-post-test-count
                              readiness
                              messages
                              label
                              average-readiness
                              average-first-pre-test-correct-count
                              average-first-post-test-correct-count]}
   options]
  [:div
   [render-learning-objective-messages messages]
  ;; user-uuid added to data-test attritube to prevent learner content from matching other learner's content
  ;; to ensure re-rendering when learner is changed. 
   [:div {:class (:learning-objective-charts-container classes) :data-test (str "user-uuid-" (get options :user-uuid))}
    [render-learning-objective-test
     {:title "Pre-Test"
      :options options
      :data {:learner-correct-count first-pre-test-correct-count
             :avg-peer-correct-count average-first-pre-test-correct-count
             :total-graded-question-count first-pre-test-count}
      :no-data-message
      (str "The Pre-Test doesn't contain any questions related to " label ".")}]
    [render-learning-objective-test
     {:title "First Post-Test"
      :options options
      :data {:learner-correct-count first-post-test-correct-count
             :avg-peer-correct-count average-first-post-test-correct-count
             :total-graded-question-count first-post-test-count}
      :no-data-message
      (str "The Post-Test doesn't contain any questions related to " label ".")}]
    [render-readiness-gauge
     {:title label
      :learner-readiness readiness
      :average-peer-readiness average-readiness}
     options]]])

(defn- render-learning-objective
  [learning-objective options]
  [:li {:class (:learning-objective-container classes)}
   [expander/render
    {:header [render-learning-objective-header learning-objective]
     :body [render-learning-objective-body learning-objective options]}]])

(defn- render-learning-objectives
  [{:keys [data options]}]
  (let [{{learner-learning-objectives :activity/learning-objectives user-uuid :user/uuid} :learner/data
         :activity/keys [learning-objectives]}
        data

        lo-engagement-lookup
        (pot/index-by :learning-objective/uuid learner-learning-objectives)]
    [:ul
     (for [{lo-uuid :learning-objective/uuid
            :as lo} (sort-by :learning-objective/ordinal learning-objectives)]
       ^{:key lo-uuid}
       [render-learning-objective
        (merge lo (get lo-engagement-lookup lo-uuid))
        (assoc options :user-uuid user-uuid)])]))

(defn- render-label
  [data]
  (when (some? data)
    [:div {:style {:padding-right (gs [:spacing :p8])}}
     [lrs/render-chart-header-label "Readiness"]]))

(defn- render-title [_]
  [lrs/render-chart-header-title "Learning Objectives"])

(defn render [data options]
  (lrs/render-chart-widget
   {:title-component render-title
    :label-component render-label
    :chart-component render-learning-objectives
    :data data
    :options options}))